import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, Typography, Paper } from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const scoreToQualitativeMap = {
  1: "5 - Bastante alejado del esperado",
  2: "4 - Desempeño por debajo del esperado",
  3: "3 - Desempeño esperado",
  4: "2 - Desempeño por encima del esperado",
  5: "1 - Excede ampliamente las expectativas",
};

export default function PrePostCalibrationComparison({ originalDistribution, calibratedDistribution }) {
  const data = useMemo(() => {
    if (!originalDistribution || Object.keys(originalDistribution).length === 0 || !calibratedDistribution || Object.keys(calibratedDistribution).length === 0) {
      return { labels: [], datasets: [] };
    }
    const labels = Object.keys(scoreToQualitativeMap).sort((a, b) => parseInt(a) - parseInt(b));

    const totalOriginal = Object.values(originalDistribution).reduce((sum, count) => sum + count, 0);
    const totalCalibrated = Object.values(calibratedDistribution).reduce((sum, count) => sum + count, 0);

    return {
      labels: labels.map(label => scoreToQualitativeMap[parseInt(label)]),
      datasets: [
        {
          label: 'Puntaje Calibrado',
          data: labels.map(label => totalCalibrated > 0 ? (calibratedDistribution[parseInt(label)] || 0) / totalCalibrated : 0),
          backgroundColor: 'rgba(53, 162, 235, 0.2)',
          borderColor: 'rgba(53, 162, 235, 1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
        {
          label: 'Puntaje Original',
          data: labels.map(label => totalOriginal > 0 ? (originalDistribution[parseInt(label)] || 0) / totalOriginal : 0),
          backgroundColor: 'rgba(255, 159, 64, 0.2)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    };
  }, [originalDistribution, calibratedDistribution]);

  const options = useMemo(() => ({
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Comparativa: Puntaje Calibrado vs. Original (Distribución)',
        font: { size: 18, weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const label = context.dataset.label || '';
            const rawCount = (context.dataset.label === 'Puntaje Calibrado') 
                             ? calibratedDistribution[parseInt(Object.keys(scoreToQualitativeMap).find(key => scoreToQualitativeMap[key] === context.label))] || 0
                             : originalDistribution[parseInt(Object.keys(scoreToQualitativeMap).find(key => scoreToQualitativeMap[key] === context.label))] || 0;
            const proportion = context.raw;
            return `${label}: ${proportion.toFixed(2)} (Total: ${rawCount})`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Puntaje',
          font: { size: 14, weight: 'bold' },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Proporción de Evaluaciones',
          font: { size: 14, weight: 'bold' },
        },
        beginAtZero: true,
        max: 0.4,
      },
    },
  }), [originalDistribution, calibratedDistribution]);

  if (!originalDistribution || Object.keys(originalDistribution).length === 0 || !calibratedDistribution || Object.keys(calibratedDistribution).length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', textAlign: 'center' }}>
        <Typography variant="subtitle1">No hay datos de comparación pre y post calibración disponibles para el período seleccionado.</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: '12px' }}>
      <Line data={data} options={options} />
    </Paper>
  );
} 